import debounce from 'lodash-es/debounce';
import { useEffect, useRef, useState } from 'react';

/**
 * スクロール状態を追跡するカスタムフック
 *
 * ウィンドウと特定のコンテナ要素のスクロールを監視し、
 * スクロール中の状態を管理します。
 *
 * @returns {boolean} isScrolling - スクロール中かどうか
 */
export const useScrollTracking = (): boolean => {
  const [isScrolling, setIsScrolling] = useState(false);
  const isScrollingRef = useRef(isScrolling);

  // 最新のstateをrefに同期
  useEffect(() => {
    isScrollingRef.current = isScrolling;
  }, [isScrolling]);

  // デバウンス関数をrefで保持
  const debouncedSetScrollingFalse = useRef(debounce(() => setIsScrolling(false), 1150)).current;

  useEffect(() => {
    const handleScroll = (): void => {
      if (!isScrollingRef.current) {
        setIsScrolling(true);
      }
      debouncedSetScrollingFalse();
    };

    // ウィンドウ全体のスクロールを監視
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 入力エリア内のスクロールも監視
    const inputScrollElements = document.querySelectorAll('[data-scroll-container]');
    for (const element of inputScrollElements) {
      element.addEventListener('scroll', handleScroll, { passive: true });
    }

    return (): void => {
      debouncedSetScrollingFalse.cancel();
      window.removeEventListener('scroll', handleScroll);
      for (const element of inputScrollElements) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [debouncedSetScrollingFalse]);

  return isScrolling;
};
